import { formatLength } from '@/lib/geometryMeasure';
import { Html } from '@react-three/drei';
import * as THREE from 'three';

type Vec3 = [number, number, number];

/**
 * Per-segment length labels for the line / polygon pickers.
 *
 * Renders a small chip at every segment's midpoint with the segment's
 * length (auto-formatted m / km). The current segment (cursor → last
 * vertex during picking) is rendered with a stronger emphasis so the
 * user can see the in-progress measurement separately from committed
 * segments.
 *
 * Uses drei `<Html>` rather than `<Text>` for the same reasons
 * SavedStockpiles does: pixel-crisp text at any zoom, no billboarding
 * artefacts, no depth-test occlusion.
 */
export const SegmentLabels = ({
  parts,
  current,
  cursor,
  closeCurrentRing = false,
  color = '#0f172a',
  background = 'rgba(255, 255, 255, 0.92)',
}: {
  /** Already-committed parts (each part rendered as its own polyline). */
  parts: Vec3[][];
  /** In-progress vertices of the current part (no closing edge). */
  current: Vec3[];
  /** Cursor preview point — drives the live trailing-segment label. */
  cursor: Vec3 | THREE.Vector3 | null;
  /** When true, also label the closing edge from `current[last]` → `current[0]`
   *  (used by the polygon tool when hovering the snap-to-first target). */
  closeCurrentRing?: boolean;
  color?: string;
  background?: string;
}) => {
  const segs: { mid: Vec3; len: number; emphasis: 'committed' | 'live' }[] = [];

  const pushSeg = (a: Vec3, b: Vec3, emphasis: 'committed' | 'live') => {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const dz = b[2] - a[2];
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (len < 0.05) return; // sub-5 cm segments are picking jitter, skip
    segs.push({
      mid: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2],
      len,
      emphasis,
    });
  };

  for (const part of parts) {
    for (let i = 1; i < part.length; i++) pushSeg(part[i - 1], part[i], 'committed');
  }
  for (let i = 1; i < current.length; i++) pushSeg(current[i - 1], current[i], 'committed');

  if (cursor && current.length > 0) {
    const last = current[current.length - 1];
    const c: Vec3 =
      Array.isArray(cursor)
        ? (cursor as Vec3)
        : [(cursor as THREE.Vector3).x, (cursor as THREE.Vector3).y, (cursor as THREE.Vector3).z];
    pushSeg(last, c, 'live');
  }

  if (closeCurrentRing && current.length >= 3) {
    pushSeg(current[current.length - 1], current[0], 'live');
  }

  return (
    <>
      {segs.map((s, i) => (
        <Html
          key={i}
          position={s.mid}
          center
          // No pointer events on the label — clicks must reach the canvas
          // so the picker keeps working with the cursor over a label.
          style={{ pointerEvents: 'none', userSelect: 'none' }}
          // `zIndexRange` keeps labels below the toolbar (which is z-20)
          // but above the canvas chrome.
          zIndexRange={[10, 1]}>
          <div
            style={{
              transform: 'translate(-50%, -100%)',
              padding: '1px 6px',
              borderRadius: 4,
              fontSize: s.emphasis === 'live' ? 12 : 11,
              fontWeight: s.emphasis === 'live' ? 600 : 500,
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
              color,
              background,
              border: '1px solid rgba(15, 23, 42, 0.12)',
              whiteSpace: 'nowrap',
              boxShadow:
                s.emphasis === 'live'
                  ? '0 1px 4px rgba(0,0,0,0.18)'
                  : '0 1px 2px rgba(0,0,0,0.10)',
              opacity: s.emphasis === 'live' ? 1 : 0.92,
            }}>
            {formatLength(s.len)}
          </div>
        </Html>
      ))}
    </>
  );
};
